import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { set } from "lodash";

export interface RelatorioVendaType {
    id: number;
    date?: string;
    status?: number;
    checked?: boolean;
    dateChecked?: string;
    empresaId?: number;
    contratoId?: number;
    vendedorNome?: string;
}

export interface CreateRelatorioVendaType {
    id: number;
    date?: string;
    status?: number;
    checked?: boolean;
    dateChecked?: string;
    empresaId?: number;
    contratoId?: number;
    relacoesAlunoVendedor?: CreateRelacaoRelatorioVendaType[];
}

export interface EditRelatorioVendaType {
    id: number;
    date?: string;
    status?: number;
    checked?: boolean;
    dateChecked?: string;
    empresaId?: number;
    contratoId?: number;
}

export interface CreateRelacaoRelatorioVendaType {
    alunoId: number;
    vendedorId: number;
    numberPhotos?: number;
    status?: number;
}

export interface EditRelacaoRelatorioVendaType {
    id: number;
    numberPhotos: number;
    status: number;
}

export interface AlunoVendedorType {
    id: number;
    alunoId: number;
    alunoNome: string;
    vendedorId: number;
    vendedorNome: string;
    cursoNome: string;
    numberPhotos: number;
    status: number;
}

export interface VendaNaoRealizadaType {
    id: number;
    relatorioVendaAlunoVendedorId: number;
    relatorioVendaId: number;
    alunoId: number;
    alunoReferenceCode: number;
    alunoNome: string;
    vendedorId: number;
    vendedorNome: string;
    cursoNome: string;
    numberPhotos?: number;
    statusRelacao?: number;
    status: number;
    observacao: string;
    createdDate?: string;
}

export interface CreateVendaNaoRealizadaType {
    relatorioVendaAlunoVendedorId: number;
    status: number;
    observacao: string;
}

export interface UpdateVendaNaoRealizadaType {
    status: number;
    observacao: string;
}

export interface VendaNaoRealizadaPagedResponse {
    items: VendaNaoRealizadaType[];
    pageCount: number;
}

export interface VendasNaoRealizadasEmpresaFilters {
    status?: number;
    vendedorId?: number;
    alunoNome?: string;
}

export interface AtualizarStatusRelatorioVendaRequest {
    alunoIds: number[];
    status: number;
}

export default function useRelatorioVenda() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listRelatorioVenda, setListRelatorioVenda] = useState<RelatorioVendaType[]>([]);
    const [listRelacaoAlunosVendedores, setListRelacaoAlunosVendedores] = useState<AlunoVendedorType[]>([]);
    const [listVendasNaoRealizadas, setListVendasNaoRealizadas] = useState<VendaNaoRealizadaType[]>([]);
    const [listVendasNaoRealizadasEmpresa, setListVendasNaoRealizadasEmpresa] = useState<VendaNaoRealizadaType[]>([]);
    const [vendasNaoRealizadasEmpresaPageCount, setVendasNaoRealizadasEmpresaPageCount] = useState<number>(0);
    const [relatorioVendaPageCount, setRelatorioVendaPageCount] = useState<number>(0);
    const [relacaoAlunosVendedoresPageCount, setRelacaoAlunosVendedoresPageCount] = useState<number>(0);
    const [initialListRelatorioVenda, setInitialListRelatorioVenda] = useState<RelatorioVendaType[]>([]);
    const [initialListRelacaoAlunosVendedores, setInitialListRelacaoAlunosVendedores] = useState<AlunoVendedorType[]>([]);
    const [relatorioVenda, setRelatorioVenda] = useState<RelatorioVendaType>({
        id: 0,
        date: "",
        status: 0,
        checked: false,
        dateChecked: "",
        empresaId: 0,
        contratoId: 0,
    });
    const [relacaoAlunosVendedores, setRelacaoAlunosVendedores] = useState<AlunoVendedorType>({
        id: 0,
        alunoId: 0,
        alunoNome: "",
        vendedorId: 0,
        vendedorNome: "",
        cursoNome: "",
        numberPhotos: 0,
        status: 0,
    });

    function createRelatorioVenda(createRelatorioVendaType: CreateRelatorioVendaType): Promise<CreateRelatorioVendaType | null> {
        setLoading(true);
        return api
            .post(`/RelatorioVenda/create`, createRelatorioVendaType)
            .then((response) => {
                const createdRelatorioVenda: CreateRelatorioVendaType = response.data;
                showToast("success", "Relatório criado com sucesso.");
                setLoading(false);
                return createdRelatorioVenda;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar relatório. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar relatório:", error);
                return null;
            });
    }

    function getRelatorioVendaPaged(
        pageNumber: number,
        RelatorioId?: number,
        EmpresaId?: number,
        ContratoId?: number,
        pageSize?: number,
        Status?: number,
        InitialDate?: string,
        FinalDate?: string,
    ): Promise<RelatorioVendaType[] | null> {
        setLoading(true);
        let url = `/RelatorioVenda/getRelatorioVendaPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (RelatorioId) {
            url += `&RelatorioId=${RelatorioId}`;
        }

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        if (ContratoId) {
            url += `&ContratoId=${ContratoId}`;
        }

        if (Status) {
            url += `&Status=${Status}`;
        }

        if (InitialDate) {
            url += `&InitialDate=${InitialDate}`;
        }

        if (FinalDate) {
            url += `&FinalDate=${FinalDate}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListRelatorioVenda(response.data.result);
                setInitialListRelatorioVenda(response.data.result);
                setRelatorioVendaPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar lista de relatórios. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar lista de relatórios:", error);
                return null;
            });
    }

    function updateRelatorioVenda(updateRelatorioVendaData: EditRelatorioVendaType): Promise<EditRelatorioVendaType | null> {
        setLoading(true);
        return api
            .put(`/RelatorioVenda/${updateRelatorioVendaData.id}`, updateRelatorioVendaData)
            .then((response) => {
                showToast("success", "Relatório atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar relatório. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar relatório:", error);
                return null;
            });
    }

    function deleteRelatorioVenda(relatorioVendaId: number): Promise<RelatorioVendaType | null> {
        setLoading(true);
        return api
            .delete(`/RelatorioVenda/${relatorioVendaId}`)
            .then(() => {
                setRelatorioVenda({
                    id: 0,
                    date: "",
                    status: 0,
                    checked: false,
                    dateChecked: "",
                    empresaId: 0,
                    contratoId: 0,
                });
                showToast("success", "Relatório deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar relatório. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar relatório:", error);
                return null;
            });
    }

    function getAlunosVendedoresByRelatorioId(relatorioVendaId: number): Promise<AlunoVendedorType[] | null> {
        setLoading(true);
        return api
            .get(`/RelatorioVendaAlunoVendedor/${relatorioVendaId}/alunos-vendedores`)
            .then((response) => {
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar alunos do relatório.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar alunos relacionados:", error);
                return null;
            });
    }

    function getAlunosVendedoresPaged(
        relatorioVendaId: number,
        pageNumber: number = 1,
        pageSize: number = 10,
        name?: string,
        alunoId?: number,
    ): Promise<AlunoVendedorType[] | null> {
        setLoading(true);

        let url = `/RelatorioVendaAlunoVendedor/alunos-vendedores-paged?RelatorioVendaId=${relatorioVendaId}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (name) {
            url += `&Name=${name}`;
        }

        if (alunoId) {
            url += `&AlunoId=${alunoId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListRelacaoAlunosVendedores(response.data.result);
                setInitialListRelacaoAlunosVendedores(response.data.result);
                setRelacaoAlunosVendedoresPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar alunos vinculados paginados.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar alunos vinculados:", error);
                return null;
            });
    }


    function adicionarRelacoesAoRelatorio(
        relatorioVendaId: number,
        relacoes: CreateRelacaoRelatorioVendaType[]
    ): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/RelatorioVendaAlunoVendedor/${relatorioVendaId}/adicionar-relacoes`, {
                relacoes,
            })
            .then((response) => {
                showToast("success", "Vínculos adicionados com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao adicionar vínculos ao relatório.", error);
                }
                setLoading(false);
                console.error("Erro ao adicionar vínculos:", error);
                return false;
            });
    }

    function updateRelacoesRelatorioVenda(updateRelacaoRelatorioVendaData: EditRelacaoRelatorioVendaType): Promise<EditRelacaoRelatorioVendaType | null> {
        setLoading(true);
        return api
            .put(`/RelatorioVendaAlunoVendedor/${updateRelacaoRelatorioVendaData.id}`, updateRelacaoRelatorioVendaData)
            .then((response) => {
                showToast("success", "Relação atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar relação. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar relação:", error);
                return null;
            });
    }

    function removerRelacaoRelatorioVenda(
        relatorioVendaId: number,
        alunoId: number,
        vendedorId: number
    ): Promise<boolean> {
        setLoading(true);
        return api
            .delete(`/RelatorioVendaAlunoVendedor/${relatorioVendaId}/${alunoId}/${vendedorId}`)
            .then(() => {
                showToast("success", "Relação removida com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao remover relação.", error);
                }
                setLoading(false);
                return false;
            });
    }

    function downloadRelatorioVendaPdf(relatorioId: number, vendedorId?: number, status?: number): Promise<void> {
        setLoading(true);

        let url = `/RelatorioVendaAlunoVendedor/${relatorioId}/pdf`;

        const queryParams: string[] = [];
        if (status !== undefined) {
            queryParams.push(`status=${status}`);
        }
        if (vendedorId !== undefined) {
            queryParams.push(`vendedorId=${vendedorId}`);
        }

        if (queryParams.length > 0) {
            url += `?${queryParams.join("&")}`;
        }

        return api
            .get(url, {
                responseType: "arraybuffer",
            })
            .then((resp) => {
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `RelatorioVenda_${relatorioId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                setLoading(false);
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao baixar PDF do relatório.", error);
                }
                setLoading(false);
            });
    }

    function downloadRelatorioReduzidoVendaPdf(relatorioId: number, vendedorId?: number, status?: number): Promise<void> {
        setLoading(true);

        let url = `/RelatorioVendaAlunoVendedor/${relatorioId}/pdfReduzido`;

        const queryParams: string[] = [];
        if (status !== undefined) {
            queryParams.push(`status=${status}`);
        }
        if (vendedorId !== undefined) {
            queryParams.push(`vendedorId=${vendedorId}`);
        }

        if (queryParams.length > 0) {
            url += `?${queryParams.join("&")}`;
        }

        return api
            .get(url, {
                responseType: "arraybuffer",
            })
            .then((resp) => {
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `RelatorioVenda_${relatorioId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                setLoading(false);
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao baixar PDF do relatório.", error);
                }
                setLoading(false);
            });
    }

    function atualizarStatusLote(relatorioVendaId: number, request: AtualizarStatusRelatorioVendaRequest): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/RelatorioVendaAlunoVendedor/${relatorioVendaId}/atualizar-status-lote`, request)
            .then(() => {
                showToast("success", "Status atualizados com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar status dos alunos.", error);
                }
                setLoading(false);
                return false;
            });
    }

    function getVendasNaoRealizadasByEmpresa(
        empresaId: number,
        page: number,
        pageSize: number,
        filters?: VendasNaoRealizadasEmpresaFilters
    ): Promise<VendaNaoRealizadaPagedResponse | null> {
        setLoading(true);
        const params: Record<string, string | number> = { page, pageSize };
        if (filters?.status) params.status = filters.status;
        if (filters?.vendedorId) params.vendedorId = filters.vendedorId;
        if (filters?.alunoNome) params.alunoNome = filters.alunoNome;

        return api
            .get(`/RelatorioVendaAlunoVendedor/vendas-nao-realizadas/empresa/${empresaId}`, { params })
            .then((response) => {
                setListVendasNaoRealizadasEmpresa(response.data.items);
                setVendasNaoRealizadasEmpresaPageCount(response.data.pageCount);
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar vendas não realizadas.", error);
                }
                setLoading(false);
                return null;
            });
    }

    function updateVendaNaoRealizada(id: number, request: UpdateVendaNaoRealizadaType): Promise<boolean> {
        setLoading(true);
        return api
            .put(`/RelatorioVendaAlunoVendedor/vendas-nao-realizadas/${id}`, request)
            .then(() => {
                showToast("success", "Tentativa atualizada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar tentativa.", error);
                }
                setLoading(false);
                return false;
            });
    }

    function getVendasNaoRealizadas(relatorioVendaId: number): Promise<VendaNaoRealizadaType[] | null> {
        setLoading(true);
        return api
            .get(`/RelatorioVendaAlunoVendedor/${relatorioVendaId}/vendas-nao-realizadas`)
            .then((response) => {
                setListVendasNaoRealizadas(response.data);
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "SessÃ£o desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar vendas nÃ£o realizadas.", error);
                }
                setLoading(false);
                return null;
            });
    }

    function createVendaNaoRealizada(request: CreateVendaNaoRealizadaType): Promise<VendaNaoRealizadaType | null> {
        setLoading(true);
        return api
            .post(`/RelatorioVendaAlunoVendedor/vendas-nao-realizadas`, request)
            .then((response) => {
                showToast("success", "Tentativa registrada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "SessÃ£o desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao registrar tentativa.", error);
                }
                setLoading(false);
                return null;
            });
    }

    function validarAlunoEmOutroRelatorioEmRota(alunoId: number): Promise<boolean> {
        setLoading(true);
        return api
            .get(`/RelatorioVendaAlunoVendedor/validar-aluno-em-rota/${alunoId}`)
            .then((response) => {
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao validar aluno em rota.", error);
                }
                setLoading(false);
                console.error("Erro ao validar aluno em rota:", error);
                return false;
            });
    }

    function validarVendedorComContratosAbertos(vendedorId: number): Promise<boolean> {
        setLoading(true);
        return api
            .get(`/RelatorioVendaAlunoVendedor/validar-vendedor-contratos-abertos/${vendedorId}`)
            .then((response) => {
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao validar vendedor com contratos abertos.", error);
                }
                setLoading(false);
                console.error("Erro ao validar vendedor com contratos abertos:", error);
                return false;
            });
    }

    function validarAlunoPertenceAoContrato(alunoId: number, contratoId: number): Promise<boolean> {
        setLoading(true);
        return api
            .get(`/RelatorioVendaAlunoVendedor/verificar-aluno-no-contrato?alunoId=${alunoId}&contratoId=${contratoId}`)
            .then((response) => {
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao verificar vínculo do aluno com o contrato.", error);
                }
                setLoading(false);
                console.error("Erro ao verificar vínculo do aluno com o contrato:", error);
                return false;
            });
    }

    function handleGetRelatorioByEmpresaId(empresaId: number): Promise<RelatorioVendaType[] | null> {
        setLoading(true);
        return api
            .get(`/RelatorioVenda/getRelatorioByEmpresaId/${empresaId}`)
            .then((response) => {
                const relatorio: RelatorioVendaType[] = response.data;
                setListRelatorioVenda(relatorio);
                setLoading(false);
                return relatorio;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações do curso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações do curso:", error);
                return null;
            });
    }

    function downloadRelatorioVendasAtivasPdf(empresaId: number, vendedorId?: number, statusRelacao?: number): Promise<void> {
        setLoading(true);

        let url = `/RelatorioVendaAlunoVendedor/pdf-vendas-ativas/${empresaId}`;

        if (vendedorId) {
            url += `?vendedorId=${vendedorId}`;
        }

        if (statusRelacao !== undefined) {
            url += `&status=${statusRelacao}`;
        }
        
        return api
            .get(url, {
                responseType: "arraybuffer",
            })
            .then((resp) => {
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                // a.download = `Relatorio_Vendas_Ativas_${empresaId}.pdf`;
                a.download = vendedorId
                    ? `Relatorio_Vendas_Ativas_${empresaId}_VENDEDOR_${vendedorId}.pdf`
                    : `Relatorio_Vendas_Ativas_${empresaId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                setLoading(false);
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao gerar relatório de vendas ativas.", error);
                }
                setLoading(false);
                console.error("Erro ao baixar relatório de vendas ativas:", error);
            });
    }

    function downloadRelatorioMultiplosPdf(empresaId: number, relatorioIds: number[]): Promise<void> {
        setLoading(true);

        return api
            .post(`/RelatorioVendaAlunoVendedor/multiplos-relatorios/pdf`, {
                empresaId,
                relatorioVendaIds: relatorioIds
            }, {
                responseType: "arraybuffer",
            })
            .then((resp) => {
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `RelatorioMultiplos_${new Date().toISOString()}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                setLoading(false);
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao gerar relatório múltiplo.", error);
                }
                setLoading(false);
            });
    }

    return {
        relatorioVenda,
        listRelatorioVenda,
        relatorioVendaPageCount,
        initialListRelatorioVenda,
        createRelatorioVenda,
        getRelatorioVendaPaged,
        updateRelatorioVenda,
        deleteRelatorioVenda,
        setRelatorioVenda,
        setListRelatorioVenda,
        setRelatorioVendaPageCount,
        getAlunosVendedoresByRelatorioId,
        getAlunosVendedoresPaged,
        adicionarRelacoesAoRelatorio,
        updateRelacoesRelatorioVenda,
        removerRelacaoRelatorioVenda,
        relacaoAlunosVendedores,
        listRelacaoAlunosVendedores,
        listVendasNaoRealizadas,
        listVendasNaoRealizadasEmpresa,
        vendasNaoRealizadasEmpresaPageCount,
        relacaoAlunosVendedoresPageCount,
        initialListRelacaoAlunosVendedores,
        downloadRelatorioVendaPdf,
        downloadRelatorioReduzidoVendaPdf,
        atualizarStatusLote,
        getVendasNaoRealizadas,
        getVendasNaoRealizadasByEmpresa,
        createVendaNaoRealizada,
        updateVendaNaoRealizada,
        validarAlunoEmOutroRelatorioEmRota,
        validarVendedorComContratosAbertos,
        validarAlunoPertenceAoContrato,
        handleGetRelatorioByEmpresaId,
        downloadRelatorioVendasAtivasPdf,
        downloadRelatorioMultiplosPdf,
    };
}
